import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, 
  Bell, 
  Shield, 
  EyeOff, 
  Camera, 
  Mail, 
  X,
  BookOpen,
  Settings as SettingsIcon,
  Save,
  Plus,
  Eye,
  Key,
  Download,
  Trash2,
  Edit,
  Lock,
  GraduationCap,
  FileText,
  Loader2,
  ShoppingBag
} from 'lucide-react';
import FileUpload, { useFileUpload } from '../../components/FileUpload';

import { useTheme } from '../../contexts/ThemeContext';
import { useExam } from '../../contexts/ExamContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import { updateUserProfile } from '../../firebase/collections';
import { auth } from '../../firebase/config';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { uploadFile } from '../../firebase/r2storage';
import toast from 'react-hot-toast';
import { extractFilenameFromUrl, downloadFileFromUrl } from '../../utils/fileUtils';
import StudentStore from '../../components/student/StudentStore';

// Student Profile Component (extracted from BatchSchedulePage)
const StudentProfileTab = ({ currentUser }) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [formData, setFormData] = useState({
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      gender: '',
      address: ''
    },
    contactInfo: {
      email: '',
      phone: '',
      alternatePhone: '',
      address: '',
      city: '',
      state: '',
      pincode: ''
    },
    parentsInfo: {
      fatherName: '',
      fatherPhone: '',
      fatherEmail: '',
      fatherOccupation: '',
      motherName: '',
      motherPhone: '',
      motherEmail: '',
      motherOccupation: ''
    },
    results: {
      class10: {
        board: '',
        year: '',
        percentage: '',
        school: '',
        documentUrl: ''
      },
      class12: {
        board: '',
        year: '',
        percentage: '',
        school: '',
        documentUrl: ''
      },
      graduation: {
        degree: '',
        university: '',
        year: '',
        percentage: '',
        documentUrl: ''
      },
      postGraduation: {
        degree: '',
        university: '',
        year: '',
        percentage: '',
        documentUrl: ''
      }
    },
    documents: {
      photo: '',
      signature: '',
      otherDocuments: []
    }
  });

  useEffect(() => {
    loadProfile();
  }, [currentUser?.uid]);

  const loadProfile = async () => {
    if (!currentUser?.uid) return;
    
    try {
      setLoading(true);
      const q = query(
        collection(db, 'studentProfiles'),
        where('studentId', '==', currentUser.uid)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        setProfileData({ id: snapshot.docs[0].id, ...data });
        setFormData({
          personalInfo: data.personalInfo || formData.personalInfo,
          contactInfo: data.contactInfo || formData.contactInfo,
          parentsInfo: data.parentsInfo || formData.parentsInfo,
          results: data.results || formData.results,
          documents: data.documents || formData.documents
        });
      } else {
        // Initialize with current user data - profile doesn't exist yet, which is fine
        setFormData(prev => ({
          ...prev,
          personalInfo: {
            ...prev.personalInfo,
            email: currentUser.email || '',
            firstName: currentUser.displayName?.split(' ')[0] || '',
            lastName: currentUser.displayName?.split(' ').slice(1).join(' ') || ''
          },
          contactInfo: {
            ...prev.contactInfo,
            email: currentUser.email || ''
          }
        }));
        // Don't show error if profile doesn't exist - it will be created when user saves
        setProfileData(null);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      // Only show error for actual errors, not when profile doesn't exist
      if (error.code === 'permission-denied') {
        toast.error('Permission denied. Please check your account permissions.');
      } else if (error.code === 'unavailable') {
        toast.error('Service temporarily unavailable. Please try again later.');
      } else {
        toast.error('Failed to load profile. Please try again.');
      }
      // Initialize with current user data even on error
      setFormData(prev => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          email: currentUser.email || '',
          firstName: currentUser.displayName?.split(' ')[0] || '',
          lastName: currentUser.displayName?.split(' ').slice(1).join(' ') || ''
        },
        contactInfo: {
          ...prev.contactInfo,
          email: currentUser.email || ''
        }
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const profileRef = profileData 
        ? doc(db, 'studentProfiles', profileData.id)
        : null;
      
      const dataToSave = {
        studentId: currentUser.uid,
        ...formData,
        updatedAt: serverTimestamp()
      };

      if (profileRef) {
        await updateDoc(profileRef, dataToSave);
      } else {
        await addDoc(collection(db, 'studentProfiles'), {
          ...dataToSave,
          createdAt: serverTimestamp()
        });
      }
      
      toast.success('Profile updated successfully');
      setEditing(false);
      await loadProfile();
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = async (file, type, subType = null) => {
    if (!file) return;
    
    setUploadingDoc(`${type}${subType ? `_${subType}` : ''}`);
    try {
      const fileName = `student_documents/${currentUser.uid}/${type}${subType ? `_${subType}` : ''}_${Date.now()}_${file.name}`;
      const result = await uploadFile(file, fileName);
      
      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }
      
      const url = result.url;
      
      if (type === 'photo') {
        setFormData(prev => ({
          ...prev,
          documents: { ...prev.documents, photo: url }
        }));
      } else if (type === 'signature') {
        setFormData(prev => ({
          ...prev,
          documents: { ...prev.documents, signature: url }
        }));
      } else if (type === 'result') {
        setFormData(prev => ({
          ...prev,
          results: {
            ...prev.results,
            [subType]: {
              ...prev.results[subType],
              documentUrl: url
            }
          }
        }));
      } else if (type === 'other') {
        setFormData(prev => ({
          ...prev,
          documents: {
            ...prev.documents,
            otherDocuments: [...(prev.documents.otherDocuments || []), { name: file.name, url }]
          }
        }));
      }
      
      toast.success('Document uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleDeleteDocument = async (type, subType = null) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      if (type === 'photo') {
        setFormData(prev => ({
          ...prev,
          documents: { ...prev.documents, photo: '' }
        }));
      } else if (type === 'signature') {
        setFormData(prev => ({
          ...prev,
          documents: { ...prev.documents, signature: '' }
        }));
      } else if (type === 'result') {
        setFormData(prev => ({
          ...prev,
          results: {
            ...prev.results,
            [subType]: {
              ...prev.results[subType],
              documentUrl: ''
            }
          }
        }));
      } else if (type === 'other') {
        setFormData(prev => ({
          ...prev,
          documents: {
            ...prev.documents,
            otherDocuments: prev.documents.otherDocuments.filter((_, index) => index !== subType)
          }
        }));
      }
      
      toast.success('Document deleted successfully');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownloadDocument = async (url, defaultFileName) => {
    if (!url) {
      toast.error('Document not available');
      return;
    }

    const fileName = extractFilenameFromUrl(url, defaultFileName);
    setShowPasswordModal(true);
    setDownloadingDoc({ url, fileName });
  };

  const verifyPasswordAndDownload = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    try {
      const { reauthenticateWithCredential, EmailAuthProvider } = await import('firebase/auth');
      const { auth } = await import('../../firebase/config');
      
      const user = auth.currentUser;
      if (!user) {
        toast.error('You must be logged in');
        return;
      }

      // Verify password
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);

      // Download file
      await downloadFileFromUrl(downloadingDoc.url, downloadingDoc.fileName);

      toast.success('Document downloaded successfully');
      setShowPasswordModal(false);
      setPassword('');
      setDownloadingDoc(null);
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        toast.error('Incorrect password');
      } else {
        toast.error('Failed to download document');
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <User className="w-6 h-6 mr-2 text-blue-600" />
          Profile
        </h2>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Edit className="w-5 h-5" />
            <span>Edit Profile</span>
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setEditing(false);
                loadProfile();
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Personal Information */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.personalInfo.firstName}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    personalInfo: { ...prev.personalInfo, firstName: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.personalInfo.firstName || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.personalInfo.lastName}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    personalInfo: { ...prev.personalInfo, lastName: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.personalInfo.lastName || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <p className="text-gray-900 dark:text-white">{formData.personalInfo.email || currentUser.email || 'Not provided'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
              {editing ? (
                <input
                  type="tel"
                  value={formData.personalInfo.phone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    personalInfo: { ...prev.personalInfo, phone: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.personalInfo.phone || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date of Birth</label>
              {editing ? (
                <input
                  type="date"
                  value={formData.personalInfo.dateOfBirth}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    personalInfo: { ...prev.personalInfo, dateOfBirth: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.personalInfo.dateOfBirth || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gender</label>
              {editing ? (
                <select
                  value={formData.personalInfo.gender}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    personalInfo: { ...prev.personalInfo, gender: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.personalInfo.gender || 'Not provided'}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
              {editing ? (
                <textarea
                  value={formData.personalInfo.address}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    personalInfo: { ...prev.personalInfo, address: e.target.value }
                  }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.personalInfo.address || 'Not provided'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              {editing ? (
                <input
                  type="email"
                  value={formData.contactInfo.email}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    contactInfo: { ...prev.contactInfo, email: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.contactInfo.email || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
              {editing ? (
                <input
                  type="tel"
                  value={formData.contactInfo.phone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    contactInfo: { ...prev.contactInfo, phone: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.contactInfo.phone || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Alternate Phone</label>
              {editing ? (
                <input
                  type="tel"
                  value={formData.contactInfo.alternatePhone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    contactInfo: { ...prev.contactInfo, alternatePhone: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.contactInfo.alternatePhone || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.contactInfo.city}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    contactInfo: { ...prev.contactInfo, city: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.contactInfo.city || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.contactInfo.state}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    contactInfo: { ...prev.contactInfo, state: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.contactInfo.state || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pincode</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.contactInfo.pincode}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    contactInfo: { ...prev.contactInfo, pincode: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.contactInfo.pincode || 'Not provided'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Parents Information */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Parents Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">Father's Details</h4>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Father's Name</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.parentsInfo.fatherName}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    parentsInfo: { ...prev.parentsInfo, fatherName: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.parentsInfo.fatherName || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Father's Phone</label>
              {editing ? (
                <input
                  type="tel"
                  value={formData.parentsInfo.fatherPhone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    parentsInfo: { ...prev.parentsInfo, fatherPhone: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.parentsInfo.fatherPhone || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Father's Email</label>
              {editing ? (
                <input
                  type="email"
                  value={formData.parentsInfo.fatherEmail}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    parentsInfo: { ...prev.parentsInfo, fatherEmail: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.parentsInfo.fatherEmail || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Father's Occupation</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.parentsInfo.fatherOccupation}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    parentsInfo: { ...prev.parentsInfo, fatherOccupation: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.parentsInfo.fatherOccupation || 'Not provided'}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3 mt-4">Mother's Details</h4>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mother's Name</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.parentsInfo.motherName}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    parentsInfo: { ...prev.parentsInfo, motherName: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.parentsInfo.motherName || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mother's Phone</label>
              {editing ? (
                <input
                  type="tel"
                  value={formData.parentsInfo.motherPhone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    parentsInfo: { ...prev.parentsInfo, motherPhone: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.parentsInfo.motherPhone || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mother's Email</label>
              {editing ? (
                <input
                  type="email"
                  value={formData.parentsInfo.motherEmail}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    parentsInfo: { ...prev.parentsInfo, motherEmail: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.parentsInfo.motherEmail || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mother's Occupation</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.parentsInfo.motherOccupation}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    parentsInfo: { ...prev.parentsInfo, motherOccupation: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{formData.parentsInfo.motherOccupation || 'Not provided'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Educational Results */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <GraduationCap className="w-5 h-5 mr-2" />
            Educational Results
          </h3>
          
          {/* Class 10th */}
          <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Class 10th</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Board</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.class10.board}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        class10: { ...prev.results.class10, board: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.class10.board || 'Not provided'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Year</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.class10.year}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        class10: { ...prev.results.class10, year: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.class10.year || 'Not provided'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Percentage</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.class10.percentage}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        class10: { ...prev.results.class10, percentage: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.class10.percentage || 'Not provided'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">School</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.class10.school}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        class10: { ...prev.results.class10, school: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.class10.school || 'Not provided'}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Document</label>
                {editing ? (
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleDocumentUpload(file, 'result', 'class10');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                    {formData.results.class10.documentUrl && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleDownloadDocument(formData.results.class10.documentUrl, 'Class10_Result')}
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </button>
                        <button
                          onClick={() => handleDeleteDocument('result', 'class10')}
                          className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : formData.results.class10.documentUrl ? (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleDownloadDocument(formData.results.class10.documentUrl, 'Class10_Result')}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download Document</span>
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">No document uploaded</p>
                )}
              </div>
            </div>
          </div>

          {/* Class 12th */}
          <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Class 12th</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Board</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.class12.board}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        class12: { ...prev.results.class12, board: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.class12.board || 'Not provided'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Year</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.class12.year}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        class12: { ...prev.results.class12, year: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.class12.year || 'Not provided'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Percentage</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.class12.percentage}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        class12: { ...prev.results.class12, percentage: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.class12.percentage || 'Not provided'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">School</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.class12.school}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        class12: { ...prev.results.class12, school: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.class12.school || 'Not provided'}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Document</label>
                {editing ? (
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleDocumentUpload(file, 'result', 'class12');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                    {formData.results.class12.documentUrl && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleDownloadDocument(formData.results.class12.documentUrl, 'Class12_Result')}
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </button>
                        <button
                          onClick={() => handleDeleteDocument('result', 'class12')}
                          className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : formData.results.class12.documentUrl ? (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleDownloadDocument(formData.results.class12.documentUrl, 'Class12_Result')}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download Document</span>
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">No document uploaded</p>
                )}
              </div>
            </div>
          </div>

          {/* Graduation */}
          <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Graduation</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Degree</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.graduation.degree}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        graduation: { ...prev.results.graduation, degree: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.graduation.degree || 'Not provided'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">University</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.graduation.university}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        graduation: { ...prev.results.graduation, university: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.graduation.university || 'Not provided'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Year</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.graduation.year}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        graduation: { ...prev.results.graduation, year: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.graduation.year || 'Not provided'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Percentage</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.graduation.percentage}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        graduation: { ...prev.results.graduation, percentage: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.graduation.percentage || 'Not provided'}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Document</label>
                {editing ? (
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleDocumentUpload(file, 'result', 'graduation');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                    {formData.results.graduation.documentUrl && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleDownloadDocument(formData.results.graduation.documentUrl, 'Graduation_Result')}
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </button>
                        <button
                          onClick={() => handleDeleteDocument('result', 'graduation')}
                          className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : formData.results.graduation.documentUrl ? (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleDownloadDocument(formData.results.graduation.documentUrl, 'Graduation_Result')}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download Document</span>
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">No document uploaded</p>
                )}
              </div>
            </div>
          </div>

          {/* Post Graduation */}
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Post Graduation</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Degree</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.postGraduation.degree}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        postGraduation: { ...prev.results.postGraduation, degree: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.postGraduation.degree || 'Not provided'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">University</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.postGraduation.university}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        postGraduation: { ...prev.results.postGraduation, university: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.postGraduation.university || 'Not provided'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Year</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.postGraduation.year}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        postGraduation: { ...prev.results.postGraduation, year: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.postGraduation.year || 'Not provided'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Percentage</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.results.postGraduation.percentage}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      results: {
                        ...prev.results,
                        postGraduation: { ...prev.results.postGraduation, percentage: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{formData.results.postGraduation.percentage || 'Not provided'}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Document</label>
                {editing ? (
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleDocumentUpload(file, 'result', 'postGraduation');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                    {formData.results.postGraduation.documentUrl && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleDownloadDocument(formData.results.postGraduation.documentUrl, 'PostGraduation_Result')}
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </button>
                        <button
                          onClick={() => handleDeleteDocument('result', 'postGraduation')}
                          className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : formData.results.postGraduation.documentUrl ? (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleDownloadDocument(formData.results.postGraduation.documentUrl, 'PostGraduation_Result')}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download Document</span>
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">No document uploaded</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Documents */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Documents
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Photo</label>
              {editing ? (
                <div className="space-y-2">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleDocumentUpload(file, 'photo');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                  {formData.documents.photo && (
                    <div className="flex items-center space-x-4">
                      <img src={formData.documents.photo} alt="Photo" className="w-24 h-24 rounded-lg object-cover border border-gray-300 dark:border-gray-600" />
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleDownloadDocument(formData.documents.photo, 'Photo')}
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </button>
                        <button
                          onClick={() => handleDeleteDocument('photo')}
                          className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : formData.documents.photo ? (
                <div className="flex items-center space-x-4">
                  <img src={formData.documents.photo} alt="Photo" className="w-24 h-24 rounded-lg object-cover border border-gray-300 dark:border-gray-600" />
                  <button
                    onClick={() => handleDownloadDocument(formData.documents.photo, 'Photo')}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">No photo uploaded</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Signature</label>
              {editing ? (
                <div className="space-y-2">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleDocumentUpload(file, 'signature');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                  {formData.documents.signature && (
                    <div className="flex items-center space-x-4">
                      <img src={formData.documents.signature} alt="Signature" className="w-32 h-16 rounded-lg object-contain border border-gray-300 dark:border-gray-600" />
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleDownloadDocument(formData.documents.signature, 'Signature')}
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </button>
                        <button
                          onClick={() => handleDeleteDocument('signature')}
                          className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : formData.documents.signature ? (
                <div className="flex items-center space-x-4">
                  <img src={formData.documents.signature} alt="Signature" className="w-32 h-16 rounded-lg object-contain border border-gray-300 dark:border-gray-600" />
                  <button
                    onClick={() => handleDownloadDocument(formData.documents.signature, 'Signature')}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">No signature uploaded</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Other Documents</label>
              {editing ? (
                <div className="space-y-2">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    multiple
                    onChange={(e) => {
                      Array.from(e.target.files).forEach(file => {
                        handleDocumentUpload(file, 'other');
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                  {formData.documents.otherDocuments?.length > 0 && (
                    <div className="space-y-2">
                      {formData.documents.otherDocuments.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <span className="text-gray-900 dark:text-white">{doc.name}</span>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDownloadDocument(doc.url, doc.name)}
                              className="flex items-center space-x-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
                            >
                              <Download className="w-4 h-4" />
                              <span>Download</span>
                            </button>
                            <button
                              onClick={() => handleDeleteDocument('other', index)}
                              className="flex items-center space-x-2 px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : formData.documents.otherDocuments?.length > 0 ? (
                <div className="space-y-2">
                  {formData.documents.otherDocuments.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <span className="text-gray-900 dark:text-white">{doc.name}</span>
                      <button
                        onClick={() => handleDownloadDocument(doc.url, doc.name)}
                        className="flex items-center space-x-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">No other documents uploaded</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Password Verification Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                <Lock className="w-5 h-5 mr-2" />
                Verify Password
              </h3>
              <button onClick={() => {
                setShowPasswordModal(false);
                setPassword('');
                setDownloadingDoc(null);
              }} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Please enter your password to download the document.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  placeholder="Enter your password"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      verifyPasswordAndDownload();
                    }
                  }}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPassword('');
                    setDownloadingDoc(null);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={verifyPasswordAndDownload}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Settings = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const { selectedExams, availableExams, addExam, removeExam } = useExam();
  const { userData, isConnected, connectionStatus, currentUser } = useRealtime();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showExamModal, setShowExamModal] = useState(false);
  const [showProfileUpload, setShowProfileUpload] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);

  // Form states
  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
    phone: '',
    bio: '',
    location: '',
    website: '',
    avatar: ''
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    testReminders: true,
    performanceUpdates: true,
    newContent: true,
    marketingEmails: false,
    soundEnabled: true,
    vibrationEnabled: true
  });

  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    showEmail: false,
    showPhone: false,
    allowMessages: true,
    showTestResults: 'friends',
    dataSharing: false
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [user, setUser] = useState({
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '+1 234 567 8900',
    location: 'New York, USA',
    birthDate: '1995-05-15',
    bio: 'Passionate student preparing for competitive exams.',
    profilePicture: null
  });

  const [documents, setDocuments] = useState([]);
  const examsSectionRef = useRef(null);

  const {
    uploadedFiles,
    uploading,
    progress,
    uploadFile,
    removeFile,
    setUploadedFiles
  } = useFileUpload({ storagePath: 'user-documents' });

  useEffect(() => {
    // Check for tab parameter in URL
    const tabParam = searchParams.get('tab');
    if (tabParam && ['profile', 'exams', 'store', 'notifications', 'privacy', 'preferences'].includes(tabParam)) {
      setActiveTab(tabParam);
      
      // If it's the exams tab, scroll to it after a delay
      if (tabParam === 'exams') {
        setTimeout(() => {
          if (examsSectionRef.current) {
            examsSectionRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 200);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    // Update form data when userData changes
    if (userData && userData.dashboard) {
      setProfileData(prev => ({
        ...prev,
        displayName: userData.dashboard.displayName || userData.dashboard.name || prev.displayName,
        email: userData.dashboard.email || prev.email,
        phone: userData.dashboard.phone || prev.phone,
        bio: userData.dashboard.bio || prev.bio,
        location: userData.dashboard.location || prev.location,
        website: userData.dashboard.website || prev.website,
        avatar: userData.dashboard.avatar || userData.dashboard.photoURL || prev.avatar
      }));
    }

    if (userData && userData.dashboard && userData.dashboard.preferences) {
      setNotificationSettings(prev => ({
        ...prev,
        emailNotifications: userData.dashboard.preferences.emailNotifications ?? prev.emailNotifications,
        pushNotifications: userData.dashboard.preferences.notifications ?? prev.pushNotifications,
        testReminders: userData.dashboard.preferences.testReminders ?? prev.testReminders,
        performanceUpdates: userData.dashboard.preferences.performanceUpdates ?? prev.performanceUpdates,
        newContent: userData.dashboard.preferences.newContent ?? prev.newContent,
        marketingEmails: userData.dashboard.preferences.marketingEmails ?? prev.marketingEmails,
        soundEnabled: userData.dashboard.preferences.soundEnabled ?? prev.soundEnabled,
        vibrationEnabled: userData.dashboard.preferences.vibrationEnabled ?? prev.vibrationEnabled
      }));

      setPrivacySettings(prev => ({
        ...prev,
        profileVisibility: userData.dashboard.preferences.profileVisibility || prev.profileVisibility,
        showEmail: userData.dashboard.preferences.showEmail ?? prev.showEmail,
        showPhone: userData.dashboard.preferences.showPhone ?? prev.showPhone,
        allowMessages: userData.dashboard.preferences.allowMessages ?? prev.allowMessages,
        showTestResults: userData.dashboard.preferences.showTestResults || prev.showTestResults,
        dataSharing: userData.dashboard.preferences.dataSharing ?? prev.dataSharing
      }));
    }
  }, [userData]);

  const handleProfileUpdate = async () => {
    setLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return;
      }

      // Get current user data to merge
      const currentData = userData && userData.dashboard ? userData.dashboard : {};

      const updateData = {
        ...currentData,
        displayName: profileData.displayName,
        name: profileData.displayName,
        phone: profileData.phone,
        bio: profileData.bio,
        location: profileData.location,
        website: profileData.website,
        avatar: profileData.avatar,
        updatedAt: new Date()
      };

      const result = await updateUserProfile(currentUser.uid, updateData);
      
      if (result.success) {
      } else {
        }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationSettingsUpdate = async () => {
    setLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return;
      }

      // Get current user data to merge preferences
      const currentData = userData && userData.dashboard ? userData.dashboard : {};
      const currentPreferences = currentData.preferences || {};

      const updateData = {
        preferences: {
          ...currentPreferences,
          emailNotifications: notificationSettings.emailNotifications,
          notifications: notificationSettings.pushNotifications,
          testReminders: notificationSettings.testReminders,
          performanceUpdates: notificationSettings.performanceUpdates,
          newContent: notificationSettings.newContent,
          marketingEmails: notificationSettings.marketingEmails,
          soundEnabled: notificationSettings.soundEnabled,
          vibrationEnabled: notificationSettings.vibrationEnabled
        },
        updatedAt: new Date()
      };

      const result = await updateUserProfile(currentUser.uid, updateData);
      
      if (result.success) {
      } else {
        }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handlePrivacySettingsUpdate = async () => {
    setLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return;
      }

      // Get current user data to merge preferences
      const currentData = userData && userData.dashboard ? userData.dashboard : {};
      const currentPreferences = currentData.preferences || {};

      const updateData = {
        preferences: {
          ...currentPreferences,
          profileVisibility: privacySettings.profileVisibility,
          showEmail: privacySettings.showEmail,
          showPhone: privacySettings.showPhone,
          allowMessages: privacySettings.allowMessages,
          showTestResults: privacySettings.showTestResults,
          dataSharing: privacySettings.dataSharing
        },
        updatedAt: new Date()
      };

      const result = await updateUserProfile(currentUser.uid, updateData);
      
      if (result.success) {
      } else {
        }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match!');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const { updatePassword, reauthenticateWithCredential, EmailAuthProvider } = await import('firebase/auth');
      const { auth } = await import('../../firebase/config');
      
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to change your password');
        return;
      }
      
      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(user.email, passwordData.currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Update password
      await updatePassword(user, passwordData.newPassword);
      
      // Clear form
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password');
      } else if (error.code === 'auth/requires-recent-login') {
        setError('Please log out and log back in before changing your password');
      } else {
        setError('Failed to change password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExamSelection = async (examId) => {
    await addExam(examId);
    setShowExamModal(false);
  };

  const handleDataExport = async () => {
    // Implement data export functionality
  };

  const handleAccountDeletion = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      // Implement account deletion
    }
  };

  const handleProfilePictureUpload = async (uploadResults) => {
    if (uploadResults.length > 0) {
      try {
        setLoading(true);
        setError(''); // Clear any previous errors
        const uploadResult = uploadResults[0];
        
        // Extract the original file from the upload result
        const file = uploadResult.file;
        
        // Check if file is valid before creating object URL
        if (!file || !(file instanceof File) && !(file instanceof Blob)) {
          setError('Invalid file selected. Please choose a valid image file.');
          return;
        }
        
        // Additional validation for file type
        if (file.type && !file.type.startsWith('image/')) {
          setError('Please select an image file (JPG, PNG, GIF, etc.)');
          return;
        }
        
        // Update profile data with new avatar using the download URL
        setProfileData(prev => ({
          ...prev,
          avatar: uploadResult.url // Use the Firebase Storage URL instead of createObjectURL
        }));
        
        
        setShowProfileUpload(false);
      } catch (error) {
        setError('Failed to upload profile picture. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDocumentUpload = (files) => {
    // Handle document uploads
  };

  const handleSaveProfile = () => {
    // Save profile logic
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'exams', name: 'Exam Preferences', icon: BookOpen },
    { id: 'store', name: 'Institute Store', icon: ShoppingBag },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'privacy', name: 'Privacy & Security', icon: Shield },
    { id: 'preferences', name: 'Preferences', icon: SettingsIcon }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div className="px-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Settings
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            Manage your account preferences and settings
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 -mx-4 sm:mx-0">
        <nav className="flex space-x-1 sm:space-x-6 lg:space-x-8 overflow-x-auto scrollbar-hide px-4 sm:px-0">
          {tabs.map((tab) => {
            // Short names for mobile
            const shortNames = {
              'profile': 'Profile',
              'exams': 'Exams',
              'store': 'Store',
              'notifications': 'Alerts',
              'privacy': 'Privacy',
              'preferences': 'Prefs'
            };
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-1.5 sm:space-x-2 py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 min-h-[44px] transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <tab.icon size={16} className="flex-shrink-0" />
                <span className="sm:hidden">{shortNames[tab.id] || tab.name}</span>
                <span className="hidden sm:inline">{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-4 sm:space-y-6">
        {/* Profile Settings */}
        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4 sm:space-y-6"
          >
            {currentUser ? (
              <StudentProfileTab currentUser={currentUser} />
            ) : (
              <div className="card p-4 sm:p-6">
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Please log in to view your profile.</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Exam Preferences */}
        {activeTab === 'exams' && (
          <motion.div
            ref={examsSectionRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4 sm:space-y-6"
          >
            <div className="card p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  Exam Preferences
                </h3>
                <button
                  onClick={() => setShowExamModal(true)}
                  className="btn-primary inline-flex items-center justify-center text-sm px-4 py-2.5 min-h-[44px] w-full sm:w-auto"
                >
                  <Plus size={16} className="mr-2 flex-shrink-0" />
                  <span>Add Exam</span>
                </button>
              </div>
              
              {selectedExams.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <BookOpen className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm sm:text-base font-medium text-gray-900 dark:text-white">No exams selected</h3>
                  <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 px-4">
                    Select the exams you're preparing for to get personalized content.
                  </p>
                  <button
                    onClick={() => setShowExamModal(true)}
                    className="mt-4 btn-primary inline-flex items-center justify-center text-sm px-4 py-2.5 min-h-[44px]"
                  >
                    <Plus size={16} className="mr-2 flex-shrink-0" />
                    <span>Select Exams</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {selectedExams.map((exam) => (
                    <div
                      key={exam.id}
                      className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                    >
                      <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 ${exam.color} rounded-lg flex items-center justify-center text-white text-sm sm:text-lg flex-shrink-0`}>
                          {exam.icon}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white text-sm sm:text-base truncate">
                            {exam.name}
                          </h4>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                            {exam.fullName}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeExam(exam.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Store Tab */}
        {activeTab === 'store' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4 sm:space-y-6"
          >
            <StudentStore />
          </motion.div>
        )}

        {/* Notification Settings */}
        {activeTab === 'notifications' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Notification Preferences
              </h3>
              
              <div className="space-y-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 dark:text-white">Email Notifications</h4>
                  {Object.entries(notificationSettings).filter(([key]) => key.includes('email') || key.includes('marketing')).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Receive notifications via email
                        </p>
                      </div>
                      <button
                        onClick={() => setNotificationSettings({...notificationSettings, [key]: !value})}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          value ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          value ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-4">Push Notifications</h4>
                  <div className="space-y-4">
                    {Object.entries(notificationSettings).filter(([key]) => !key.includes('email') && !key.includes('marketing')).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Receive push notifications
                          </p>
                        </div>
                        <button
                          onClick={() => setNotificationSettings({...notificationSettings, [key]: !value})}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            value ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            value ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Recent Notifications
                </h3>
                <span className="text-sm text-gray-500">
                  {Array.isArray(userData?.notifications) ? userData.notifications.length : 0} items
                </span>
              </div>

              {!userData?.notifications?.length && (
                <p className="text-gray-600 dark:text-gray-400">No notifications yet.</p>
              )}

              <div className="space-y-3">
                {userData?.notifications?.map((n) => {
                  const createdAt = n.createdAt?.toDate ? n.createdAt.toDate() : (n.createdAt || null);
                  return (
                    <div
                      key={n.id || `${n.title || ''}-${n.createdAt || ''}`}
                      className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {n.title || 'Notification'}
                          </p>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                            {n.body || n.message || n.description || 'Tap to view details.'}
                          </p>
                        </div>
                        {createdAt && (
                          <span className="text-xs text-gray-500">
                            {new Date(createdAt).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {/* Privacy & Security */}
        {activeTab === 'privacy' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Password Change */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Change Password
              </h3>
              
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <button
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <button
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handlePasswordChange}
                    disabled={loading}
                    className="btn-primary inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Changing Password...
                      </>
                    ) : (
                      <>
                        <Key size={16} className="mr-2" />
                        Change Password
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Privacy Settings */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Privacy Settings
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Profile Visibility
                  </label>
                  <select
                    value={privacySettings.profileVisibility}
                    onChange={(e) => setPrivacySettings({...privacySettings, profileVisibility: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="public">Public</option>
                    <option value="friends">Friends Only</option>
                    <option value="private">Private</option>
                  </select>
                </div>

                <div className="space-y-4">
                  {Object.entries(privacySettings).filter(([key]) => key !== 'profileVisibility').map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Control your privacy settings
                        </p>
                      </div>
                      <button
                        onClick={() => setPrivacySettings({...privacySettings, [key]: !value})}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          value ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          value ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Preferences */}
        {activeTab === 'preferences' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Theme Settings */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Appearance
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Dark Mode
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Switch between light and dark themes
                    </p>
                  </div>
                  <button
                    onClick={toggleDarkMode}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      darkMode ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      darkMode ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Data Management */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Data Management
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Export Data
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Download all your data in JSON format
                    </p>
                  </div>
                  <button
                    onClick={handleDataExport}
                    className="btn-secondary inline-flex items-center"
                  >
                    <Download size={16} className="mr-2" />
                    Export
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div>
                    <p className="font-medium text-red-900 dark:text-red-100">
                      Delete Account
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      Permanently delete your account and all data
                    </p>
                  </div>
                  <button
                    onClick={handleAccountDeletion}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg inline-flex items-center"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Exam Selection Modal */}
      {showExamModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowExamModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Add Exam
              </h3>
              <button
                onClick={() => setShowExamModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {availableExams
                .filter(exam => !selectedExams.find(selected => selected.id === exam.id))
                .map((exam) => (
                  <button
                    key={exam.id}
                    onClick={() => handleExamSelection(exam.id)}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-200 text-left"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-12 h-12 ${exam.color} rounded-lg flex items-center justify-center text-white text-xl`}>
                        {exam.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {exam.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {exam.fullName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {exam.description}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Profile Picture Upload Modal */}
      {showProfileUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Upload Profile Picture</h2>
              <button
                onClick={() => setShowProfileUpload(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <FileUpload
              onFilesUploaded={handleProfilePictureUpload}
              multiple={false}
              maxFiles={1}
              maxSize={5 * 1024 * 1024} // 5MB
              storagePath="profile-pictures"
              showPreview={true}
              showProgress={true}
              showActions={false}
              placeholder="Click to select profile picture"
              allowedTypes={['image/*']}
            />

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowProfileUpload(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowProfileUpload(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocumentUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Upload Documents</h2>
              <button
                onClick={() => setShowDocumentUpload(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <FileUpload
              onFilesUploaded={handleDocumentUpload}
              multiple={true}
              maxFiles={10}
              maxSize={50 * 1024 * 1024} // 50MB
              storagePath="user-documents"
              showPreview={true}
              showProgress={true}
              showActions={true}
              placeholder="Drag and drop documents here, or click to select"
              allowedTypes={[
                'image/*', 
                'application/pdf', 
                'text/*', 
                'application/*',
                'audio/*',
                'video/*'
              ]}
            />

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowDocumentUpload(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowDocumentUpload(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
